import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, IconButton, Chip, InputAdornment, MenuItem,
  Tooltip, alpha, Avatar, Divider, Skeleton, Tabs, Tab, ToggleButton, ToggleButtonGroup,
  Checkbox, CircularProgress, FormControlLabel, Collapse
} from '@mui/material';
import {
  Add, Search, Receipt, Close, Visibility, Payment, PictureAsPdf, CheckCircle,
  Edit, Delete, Business, People, Send, MarkEmailRead, ArrowBack, ArrowForward,
  KeyboardArrowDown, KeyboardArrowRight, NotificationsActive, AlarmAdd, FolderOpen
} from '@mui/icons-material';
import { providerInvoicesAPI, contractorInvoicesAPI, providersAPI, contractorsAPI, remindersAPI, collectionsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { fullName } from '../../utils/formatters';
import { useNavigate } from 'react-router-dom';
import { useTableSort } from '../../components/useTableSort';

const fmt = v => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(v) || 0);

const statusColors = {
  draft: { bg: '#f1f5f9', color: '#64748b' },
  generated: { bg: alpha('#3b82f6', 0.1), color: '#2563eb' },
  approved: { bg: alpha('#8b5cf6', 0.1), color: '#7c3aed' },
  sent: { bg: alpha('#f59e0b', 0.1), color: '#d97706' },
  paid: { bg: alpha('#10b981', 0.1), color: '#059669' },
  partial: { bg: alpha('#8b5cf6', 0.1), color: '#7c3aed' },
  overdue: { bg: alpha('#ef4444', 0.1), color: '#dc2626' },
  cancelled: { bg: alpha('#ef4444', 0.1), color: '#dc2626' }
};

const Invoices = () => {
  const navigate = useNavigate();
  const [providerInvoices, setProviderInvoices] = useState([]);
  const [contractorInvoices, setContractorInvoices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [includeInactiveProviders, setIncludeInactiveProviders] = useState(false);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [selectedProviderObj, setSelectedProviderObj] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(20);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Generate dialog — multi-step
  const [genOpen, setGenOpen] = useState(false);
  const [genStep, setGenStep] = useState(0);
  const [genType, setGenType] = useState('provider');
  const [genEntity, setGenEntity] = useState('');
  const [genStartDate, setGenStartDate] = useState('');
  const [genEndDate, setGenEndDate] = useState('');
  const [genDueDate, setGenDueDate] = useState('');
  const [genNotes, setGenNotes] = useState('');
  const [genLoading, setGenLoading] = useState(false);
  const [pendingCollections, setPendingCollections] = useState([]);
  const [selectedCollections, setSelectedCollections] = useState(new Set());
  const [previewData, setPreviewData] = useState(null);
  const [editedItems, setEditedItems] = useState([]);

  // View dialog
  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewType, setViewType] = useState('provider');

  // Payment dialog
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', paymentDate: '', paymentMethod: 'check', referenceNumber: '', notes: '' });
  const [payId, setPayId] = useState(null);
  const [payType, setPayType] = useState('provider');

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ notes: '', dueDate: '', status: '', invoiceDate: '', subtotal: '' });
  const [editId, setEditId] = useState(null);
  const [editType, setEditType] = useState('provider');

  // Delete dialog
  const [deleteId, setDeleteId] = useState(null);
  const [deleteType, setDeleteType] = useState('provider');

  // Entity detail popup
  const [entityPopup, setEntityPopup] = useState(null);

  // Collection detail popup
  const [collectionPopup, setCollectionPopup] = useState(null);
  const [collectionLoading, setCollectionLoading] = useState(false);

  // Split by month option
  const [genSplitByMonth, setGenSplitByMonth] = useState(true);

  // Action loading states
  const [actionLoading, setActionLoading] = useState({});

  // Reminder dialog
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderForm, setReminderForm] = useState({ title: '', description: '', reminderDate: '', priority: 'medium' });
  const [reminderInvoice, setReminderInvoice] = useState(null);

  const uniqById = useCallback((list) => {
    const seen = new Set();
    const out = [];
    for (const item of (Array.isArray(list) ? list : [])) {
      const id = item?._id ?? item?.id;
      if (!id) continue;
      const key = String(id);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(item);
    }
    return out;
  }, []);

  const sortProvidersByName = useCallback((list) => {
    return [...(Array.isArray(list) ? list : [])].sort((a, b) => {
      const an = (fullName(a) || '').toLowerCase();
      const bn = (fullName(b) || '').toLowerCase();
      return an.localeCompare(bn);
    });
  }, []);

  const loadProviders = useCallback(async (includeInactive) => {
    setProvidersLoading(true);
    try {
      const params = { limit: 10000, includeDetails: false };
      if (!includeInactive) params.isActive = true;
      const prov = await providersAPI.getAll(params);
      const list = uniqById(Array.isArray(prov.data) ? prov.data : prov.data?.data || []);
      setProviders(sortProvidersByName(list));
    } catch (e) {
      console.error(e);
      toast.error('Failed to load providers');
    } finally {
      setProvidersLoading(false);
    }
  }, [sortProvidersByName, uniqById]);

  const providerOptions = useMemo(() => {
    const base = includeInactiveProviders ? providers : providers.filter(p => p.isActive !== false);
    if (includeInactiveProviders) return base;
    const injected = [];
    if (selectedProviderObj && selectedProviderObj.isActive === false) injected.push(selectedProviderObj);
    const merged = uniqById([...injected, ...base]);
    return sortProvidersByName(merged);
  }, [providers, includeInactiveProviders, selectedProviderObj, sortProvidersByName, uniqById]);

  const load = useCallback(async () => {
    try {
      const [pi, ci, ct] = await Promise.all([
        providerInvoicesAPI.getAll({ limit: 1000 }),
        contractorInvoicesAPI.getAll({ limit: 1000 }),
        contractorsAPI.getAll({ limit: 1000 })
      ]);
      setProviderInvoices(Array.isArray(pi.data) ? pi.data : pi.data?.data || []);
      setContractorInvoices(Array.isArray(ci.data) ? ci.data : ci.data?.data || []);
      setContractors(Array.isArray(ct.data) ? ct.data : ct.data?.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { document.title = 'Invoices | MyPayPlus'; }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadProviders(includeInactiveProviders); }, [includeInactiveProviders, loadProviders]);

  const allInvoices = [
    ...providerInvoices.map(inv => ({
      ...inv, _id: inv._id || inv.id, _type: 'provider',
      _entityName: fullName(inv.provider), _entityId: inv.provider?.providerId,
      _entityObj: inv.provider,
      _collectionId: inv.collectionIdStr || inv.collection?.collectionId || '—',
      _totalCollection: (inv.items || []).reduce((s, it) => s + (parseFloat(it.collectionAmount) || 0), 0),
      _paymentId: inv.paymentId || (inv.payments && inv.payments.length > 0 ? inv.payments[0].paymentId : null)
    })),
    ...contractorInvoices.map(inv => {
      let cIds = inv.collectionIds;
      if (typeof cIds === 'string') try { cIds = JSON.parse(cIds); } catch { cIds = []; }
      return {
        ...inv, _id: inv._id || inv.id, _type: 'contractor',
        _entityName: fullName(inv.contractor), _entityId: inv.contractor?.contractorId,
        _entityObj: inv.contractor,
        _collectionId: (Array.isArray(cIds) ? cIds : []).join(', ') || '—',
        _totalCollection: (inv.items || []).reduce((s, it) => s + (parseFloat(it.collectionAmount) || 0), 0),
        _paymentId: inv.paymentId || (inv.payments && inv.payments.length > 0 ? inv.payments[0].paymentId : null)
      };
    })
  ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const filtered = allInvoices.filter(r => {
    if (typeFilter !== 'all' && r._type !== typeFilter) return false;
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    const s = [r._entityName || '', r.invoiceNumber || '', r._collectionId || '', r._paymentId || ''].join(' ').toLowerCase();
    return s.includes(search.toLowerCase());
  });

  const invoiceColumns = useMemo(() => [
    { id: '_expand', label: '', getValue: () => '', sortable: false, filterable: false, headerSx: { width: 30 } },
    { id: 'invoiceNumber', label: 'Invoice #', getValue: r => r.invoiceNumber || '' },
    { id: '_type', label: 'Type', getValue: r => r._type === 'provider' ? 'Provider' : 'Contractor' },
    { id: '_entityName', label: 'Provider / Contractor', getValue: r => r._entityName || '' },
    { id: '_collectionId', label: 'Collection', getValue: r => r._collectionId || '' },
    { id: 'invoiceDate', label: 'Date', getValue: r => r.invoiceDate || r.createdAt || '', getDisplay: r => r.invoiceDate ? new Date(r.invoiceDate).toLocaleDateString() : r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—' },
    { id: 'generationType', label: 'Generated', getValue: r => r.generationType === 'bulk' ? 'Bulk' : 'Manual' },
    { id: '_totalCollection', label: 'Total Collection', getValue: r => parseFloat(r._totalCollection) || 0, align: 'right' },
    { id: 'total', label: 'Amount', getValue: r => parseFloat(r.total) || 0, align: 'right' },
    { id: 'amountPaid', label: 'Paid', getValue: r => parseFloat(r.amountPaid) || 0, align: 'right' },
    { id: 'balance', label: 'Balance', getValue: r => parseFloat(r.balance) || 0, align: 'right' },
    { id: '_paymentId', label: 'Payment ID', getValue: r => r._paymentId || '' },
    { id: 'status', label: 'Status', getValue: r => r.status || 'draft' },
    { id: 'sent', label: 'Sent', getValue: r => r.sentAt ? 'Yes' : 'No', align: 'center' },
  ], []);

  const { sortedData: sortedFiltered, renderHeader: renderInvHeader, renderFilterPopover: renderInvFilterPopover, renderClearFilters: renderInvClearFilters } = useTableSort(invoiceColumns, filtered, { defaultSort: 'invoiceDate', defaultDir: 'desc' });

  const stats = {
    total: allInvoices.length, provider: providerInvoices.length, contractor: contractorInvoices.length,
    totalAmount: allInvoices.reduce((s, i) => s + (parseFloat(i.total) || 0), 0),
    totalPaid: allInvoices.reduce((s, i) => s + (parseFloat(i.amountPaid) || 0), 0),
    totalBalance: allInvoices.reduce((s, i) => s + (parseFloat(i.balance) || 0), 0),
  };

  /* ── Generate Flow ── */
  const resetGen = () => {
    setGenStep(0); setGenType('provider'); setGenEntity(''); setGenStartDate(''); setGenEndDate('');
    setGenDueDate(''); setGenNotes(''); setPendingCollections([]); setSelectedCollections(new Set());
    setPreviewData(null); setEditedItems([]); setGenLoading(false); setGenSplitByMonth(true);
  };

  const handleFindCollections = async () => {
    if (!genEntity) return toast.error(`Please select a ${genType}`);
    setGenLoading(true);
    try {
      const api = genType === 'provider' ? providerInvoicesAPI : contractorInvoicesAPI;
      const params = {};
      if (genStartDate) params.startDate = genStartDate;
      if (genEndDate) params.endDate = genEndDate;
      const res = await api.getPendingCollections(params);
      const cols = Array.isArray(res.data) ? res.data : [];
      const mine = genType === 'provider'
        ? cols.filter(c => String(c.providerId) === String(genEntity))
        : cols.filter(c => String(c.contractorId) === String(genEntity) || (c.services && c.services.some(s => String(s.contractorId) === String(genEntity))));
      setPendingCollections(mine);
      setSelectedCollections(new Set(mine.map(c => c.id || c._id)));
      setGenStep(1);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to fetch collections'); }
    finally { setGenLoading(false); }
  };

  const handlePreview = async () => {
    const ids = [...selectedCollections];
    if (!ids.length) return toast.error('Select at least one collection');
    setGenLoading(true);
    try {
      if (genType === 'contractor') {
        const res = await contractorInvoicesAPI.preview({ contractorId: genEntity, collectionIds: ids, startDate: genStartDate, endDate: genEndDate });
        setPreviewData(res.data);
        setEditedItems((res.data.items || []).map(it => ({ ...it })));
      } else {
        const selected = pendingCollections.filter(c => selectedCollections.has(c.id || c._id));
        const items = selected.flatMap(c => (c.services || []).map(svc => ({
          collectionId: c.collectionId, serviceName: svc.service?.name || svc.serviceName || 'Service',
          collectionAmount: svc.collectionAmount, rate: svc.billingRate, amount: svc.billingAmount
        })));
        setPreviewData({ collections: selected, items, total: items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0) });
        setEditedItems([]);
      }
      setGenStep(2);
    } catch (e) { toast.error(e.response?.data?.message || 'Preview failed'); }
    finally { setGenLoading(false); }
  };

  const handleGenerate = async () => {
    const ids = [...selectedCollections];
    if (!ids.length) return toast.error('No collections selected');
    setGenLoading(true);
    try {
      if (genType === 'provider') {
        await providerInvoicesAPI.bulkGenerate({ collectionIds: ids, dueDate: genDueDate || undefined, notes: genNotes || undefined, splitByMonth: genSplitByMonth });
      } else {
        await contractorInvoicesAPI.generate({
          contractorId: genEntity, collectionIds: ids, startDate: genStartDate, endDate: genEndDate,
          dueDate: genDueDate || undefined, notes: genNotes || undefined,
          editedItems: editedItems.length > 0 ? editedItems : undefined
        });
      }
      setGenOpen(false); resetGen(); load();
      toast.success(`${genType === 'provider' ? 'Invoice(s)' : 'Pay stub'} generated successfully`);
    } catch (e) { toast.error(e.response?.data?.message || 'Generation failed'); }
    finally { setGenLoading(false); }
  };

  const toggleCol = id => setSelectedCollections(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAllCols = () => setSelectedCollections(prev => prev.size === pendingCollections.length ? new Set() : new Set(pendingCollections.map(c => c.id || c._id)));

  /* ── Other handlers ── */
  const handleView = async (inv) => {
    try {
      const api = inv._type === 'provider' ? providerInvoicesAPI : contractorInvoicesAPI;
      const res = await api.getById(inv._id);
      setViewData(res.data); setViewType(inv._type); setViewOpen(true);
    } catch (e) { toast.error('Failed to load invoice'); }
  };

  const handlePayment = async () => {
    setActionLoading(prev => ({ ...prev, paying: true }));
    try {
      const api = payType === 'provider' ? providerInvoicesAPI : contractorInvoicesAPI;
      await api.addPayment(payId, payForm);
      setPayOpen(false); setPayId(null); load();
      toast.success('Payment recorded — status updated automatically');
    } catch (e) { toast.error(e.response?.data?.message || 'Payment failed'); }
    finally { setActionLoading(prev => ({ ...prev, paying: false })); }
  };

  const handleEdit = async () => {
    setActionLoading(prev => ({ ...prev, editing: true }));
    try {
      const api = editType === 'provider' ? providerInvoicesAPI : contractorInvoicesAPI;
      const data = { ...editForm };
      if (data.subtotal) { data.subtotal = parseFloat(data.subtotal); data.total = data.subtotal; }
      else { delete data.subtotal; delete data.total; }
      await api.update(editId, data);
      setEditOpen(false); setEditId(null); load();
      toast.success('Invoice updated');
    } catch (e) { toast.error(e.response?.data?.message || 'Update failed'); }
    finally { setActionLoading(prev => ({ ...prev, editing: false })); }
  };

  const handleDelete = async () => {
    setActionLoading(prev => ({ ...prev, deleting: true }));
    try {
      const api = deleteType === 'provider' ? providerInvoicesAPI : contractorInvoicesAPI;
      await api.delete(deleteId);
      setDeleteId(null); load();
      toast.success('Invoice deleted');
    } catch (e) { toast.error(e.response?.data?.message || 'Delete failed'); }
    finally { setActionLoading(prev => ({ ...prev, deleting: false })); }
  };

  const handleDownloadPdf = async (inv) => {
    try {
      const api = inv._type === 'provider' ? providerInvoicesAPI : contractorInvoicesAPI;
      const res = await api.downloadPDF(inv._id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv._type === 'provider' ? 'invoice' : 'paystub'}-${inv.invoiceNumber}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('PDF download failed'); }
  };

  const handleToggleSent = async (inv) => {
    const key = `sent-${inv._type}-${inv._id}`;
    setActionLoading(prev => ({ ...prev, [key]: true }));
    try {
      const api = inv._type === 'provider' ? providerInvoicesAPI : contractorInvoicesAPI;
      await api.toggleSent(inv._id);
      load();
    } catch (e) { toast.error('Failed to update sent status'); }
    finally { setActionLoading(prev => ({ ...prev, [key]: false })); }
  };

  const handleCollectionClick = async (collectionIdStr) => {
    if (!collectionIdStr || collectionIdStr === '—') return;
    const ids = collectionIdStr.split(',').map(s => s.trim()).filter(Boolean);
    if (!ids.length) return;
    setCollectionLoading(true);
    try {
      // Fetch all collections and find by collectionId string
      const all = await collectionsAPI.getAll({ limit: 5000 });
      const allCols = Array.isArray(all.data) ? all.data : all.data?.data || [];
      const match = allCols.find(c => ids.includes(c.collectionId));
      if (match) {
        // Now fetch full details by numeric id
        const detail = await collectionsAPI.getById(match._id || match.id);
        setCollectionPopup(detail.data || match);
      } else {
        toast.error('Collection not found');
      }
    } catch { toast.error('Could not load collection details'); }
    finally { setCollectionLoading(false); }
  };

  const openEditDialog = (inv) => {
    setEditId(inv._id); setEditType(inv._type);
    setEditForm({
      notes: inv.notes || '',
      dueDate: inv.dueDate ? new Date(inv.dueDate).toISOString().slice(0, 10) : '',
      invoiceDate: inv.invoiceDate ? new Date(inv.invoiceDate).toISOString().slice(0, 10) : '',
      status: inv.status || 'generated',
      subtotal: inv.subtotal || inv.total || ''
    });
    setEditOpen(true);
  };

  const toggleRowExpand = (id) => setExpandedRows(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const handleEntityClick = (entity, type) => {
    if (!entity) return;
    setEntityPopup({ ...entity, _entityType: type });
  };

  const handleSetReminder = (inv) => {
    setReminderInvoice(inv);
    setReminderForm({
      title: `Follow up: ${inv.invoiceNumber}`,
      description: `Follow up on ${inv._type === 'provider' ? 'Invoice' : 'Pay Stub'} ${inv.invoiceNumber} for ${inv._entityName}`,
      reminderDate: inv.dueDate ? new Date(inv.dueDate).toISOString().slice(0, 10) : new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
      priority: 'medium'
    });
    setReminderOpen(true);
  };

  const handleSaveReminder = async () => {
    try {
      await remindersAPI.create({
        ...reminderForm,
        type: 'invoice_followup',
        relatedModel: reminderInvoice._type === 'provider' ? 'ProviderInvoice' : 'ContractorInvoice',
        relatedId: reminderInvoice._id,
        relatedInvoiceNumber: reminderInvoice.invoiceNumber
      });
      setReminderOpen(false);
      toast.success('Reminder set successfully');
    } catch (e) { toast.error('Failed to set reminder'); }
  };

  const entityList = genType === 'provider' ? providerOptions : contractors;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">Invoices</Typography>
          <Typography variant="body2" color="text.secondary">Manage provider invoices and contractor pay stubs</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button variant="outlined" startIcon={<Add />} onClick={() => navigate('/collections')}
            sx={{ borderColor: '#f59e0b', color: '#d97706', fontWeight: 700, '&:hover': { borderColor: '#d97706', bgcolor: alpha('#f59e0b', 0.06) } }}>
            Add Collection
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => { resetGen(); setGenOpen(true); }}
            sx={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', boxShadow: '0 4px 14px rgba(59,130,246,0.3)', px: 3, fontWeight: 700 }}>
            Generate Invoice
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Invoices', value: stats.total, sub: `${stats.provider} Provider / ${stats.contractor} Contractor`, color: '#3b82f6' },
          { label: 'Total Amount', value: fmt(stats.totalAmount), sub: 'All invoices', color: '#8b5cf6' },
          { label: 'Total Paid', value: fmt(stats.totalPaid), sub: 'Payments received', color: '#10b981' },
          { label: 'Outstanding', value: fmt(stats.totalBalance), sub: 'Remaining balance', color: '#ef4444' },
        ].map((s, i) => (
          <Grid item xs={6} md={3} key={i}>
            <Card sx={{ borderLeft: `4px solid ${s.color}` }}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</Typography>
                <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color: s.color, mt: 0.5 }}>{s.value}</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.3 }}>{s.sub}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Main Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Search invoices..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20, color: '#94a3b8' }} /></InputAdornment> }}
              sx={{ width: 260 }} />
            <ToggleButtonGroup size="small" exclusive value={typeFilter}
              onChange={(_, v) => { if (v) { setTypeFilter(v); setPage(0); } }}
              sx={{ '& .MuiToggleButton-root': { px: 2, py: 0.5, fontSize: '0.78rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px !important' } }}>
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="provider" sx={{ '&.Mui-selected': { bgcolor: alpha('#3b82f6', 0.12), color: '#2563eb' } }}>
                <Business sx={{ fontSize: 16, mr: 0.5 }} /> Provider
              </ToggleButton>
              <ToggleButton value="contractor" sx={{ '&.Mui-selected': { bgcolor: alpha('#8b5cf6', 0.12), color: '#7c3aed' } }}>
                <People sx={{ fontSize: 16, mr: 0.5 }} /> Contractor
              </ToggleButton>
            </ToggleButtonGroup>
            <Tabs value={statusFilter} onChange={(_, v) => { setStatusFilter(v); setPage(0); }}
              sx={{ '& .MuiTab-root': { textTransform: 'capitalize', fontWeight: 600, fontSize: '0.78rem', minHeight: 34, py: 0, minWidth: 'auto', px: 1.5 },
                '& .MuiTabs-indicator': { bgcolor: '#3b82f6' } }}>
              <Tab value="all" label="All" />
              <Tab value="generated" label="Generated" />
              <Tab value="sent" label="Sent" />
              <Tab value="paid" label="Paid" />
              <Tab value="partial" label="Partial" />
              <Tab value="overdue" label="Overdue" />
            </Tabs>
            {renderInvClearFilters()}
          </Box>

          {loading ? (
            <Box sx={{ p: 3 }}>{[...Array(5)].map((_, i) => <Skeleton key={i} height={52} sx={{ mb: 0.5 }} />)}</Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  {renderInvHeader(<TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Actions</TableCell>)}
                  <TableBody>
                    {sortedFiltered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={15} sx={{ py: 6, textAlign: 'center' }}>
                          <Receipt sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                          <Typography color="text.secondary">No invoices found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : sortedFiltered.slice(page * rpp, page * rpp + rpp).map(r => {
                      const sc = statusColors[r.status] || statusColors.draft;
                      const isP = r._type === 'provider';
                      const hasItems = r.items && r.items.length > 1;
                      const isExpanded = expandedRows.has(`${r._type}-${r._id}`);
                      return (
                        <React.Fragment key={`${r._type}-${r._id}`}>
                          <TableRow hover sx={{ '&:hover': { bgcolor: alpha(isP ? '#3b82f6' : '#8b5cf6', 0.03) } }}>
                            <TableCell sx={{ py: 0.5, px: 0.5 }}>
                              {hasItems && (
                                <IconButton size="small" onClick={() => toggleRowExpand(`${r._type}-${r._id}`)} sx={{ p: 0.3 }}>
                                  {isExpanded ? <KeyboardArrowDown sx={{ fontSize: 18 }} /> : <KeyboardArrowRight sx={{ fontSize: 18 }} />}
                                </IconButton>
                              )}
                            </TableCell>
                            <TableCell><Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: isP ? '#3b82f6' : '#8b5cf6', fontFamily: 'monospace' }}>{r.invoiceNumber}</Typography></TableCell>
                            <TableCell>
                              <Chip icon={isP ? <Business sx={{ fontSize: 14 }} /> : <People sx={{ fontSize: 14 }} />}
                                label={isP ? 'Invoice' : 'Pay Stub'} size="small"
                                sx={{ fontWeight: 700, height: 24, bgcolor: isP ? alpha('#3b82f6', 0.08) : alpha('#8b5cf6', 0.08), color: isP ? '#2563eb' : '#7c3aed', '& .MuiChip-icon': { color: 'inherit' } }} />
                            </TableCell>
                            <TableCell>
                              <Typography
                                onClick={() => handleEntityClick(r._entityObj, r._type)}
                                sx={{ fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', color: '#3b82f6', '&:hover': { textDecoration: 'underline' } }}>
                                {r._entityName || 'N/A'}
                              </Typography>
                              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontFamily: 'monospace' }}>{r._entityId || ''}</Typography>
                            </TableCell>
                            <TableCell>
                              {r._collectionId !== '—' ? (
                                <Typography
                                  onClick={() => handleCollectionClick(r._collectionId)}
                                  sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: '#d97706', cursor: 'pointer', '&:hover': { textDecoration: 'underline', color: '#b45309' } }}>
                                  {collectionLoading ? <CircularProgress size={12} /> : r._collectionId}
                                </Typography>
                              ) : (
                                <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>—</Typography>
                              )}
                            </TableCell>
                            <TableCell><Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{r.invoiceDate ? new Date(r.invoiceDate).toLocaleDateString() : '—'}</Typography></TableCell>
                            <TableCell><Chip label={r.generationType === 'bulk' ? 'Bulk' : 'Manual'} size="small" sx={{ fontWeight: 700, height: 22, fontSize: '0.7rem', bgcolor: r.generationType === 'bulk' ? alpha('#7c3aed', 0.1) : alpha('#64748b', 0.1), color: r.generationType === 'bulk' ? '#7c3aed' : '#64748b' }} /></TableCell>
                            <TableCell align="right"><Typography sx={{ fontSize: '0.8rem', color: '#0891b2' }}>{fmt(r._totalCollection)}</Typography></TableCell>
                            <TableCell align="right"><Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>{fmt(r.total)}</Typography></TableCell>
                            <TableCell align="right"><Typography sx={{ fontSize: '0.8rem', color: '#059669' }}>{fmt(r.amountPaid)}</Typography></TableCell>
                            <TableCell align="right"><Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: parseFloat(r.balance) > 0 ? '#dc2626' : '#059669' }}>{fmt(r.balance)}</Typography></TableCell>
                            <TableCell><Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: r._paymentId ? '#059669' : '#94a3b8' }}>{r._paymentId || '—'}</Typography></TableCell>
                            <TableCell><Chip label={r.status || 'draft'} size="small" sx={{ fontWeight: 700, height: 24, bgcolor: sc.bg, color: sc.color, textTransform: 'capitalize' }} /></TableCell>
                            <TableCell align="center">
                              {actionLoading[`sent-${r._type}-${r._id}`] ? (
                                <CircularProgress size={16} sx={{ color: '#94a3b8' }} />
                              ) : (
                                <Tooltip title={r.sent ? `Sent ${r.sentAt ? new Date(r.sentAt).toLocaleDateString() : ''}` : 'Mark as sent'}>
                                  <IconButton size="small" onClick={() => handleToggleSent(r)}
                                    sx={{ color: r.sent ? '#059669' : '#cbd5e1', '&:hover': { color: r.sent ? '#dc2626' : '#059669' } }}>
                                    {r.sent ? <MarkEmailRead sx={{ fontSize: 18 }} /> : <Send sx={{ fontSize: 16 }} />}
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.3 }}>
                                <Tooltip title="View"><IconButton size="small" onClick={() => handleView(r)} sx={{ color: '#3b82f6' }}><Visibility sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                                <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditDialog(r)} sx={{ color: '#f59e0b' }}><Edit sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                                <Tooltip title="Record Payment"><IconButton size="small" onClick={() => {
                                  setPayId(r._id); setPayType(r._type);
                                  setPayForm({ amount: r.balance || '', paymentDate: new Date().toISOString().slice(0, 10), paymentMethod: 'check', referenceNumber: '', notes: '' }); setPayOpen(true);
                                }} sx={{ color: '#10b981' }}><Payment sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                                <Tooltip title="Set Reminder"><IconButton size="small" onClick={() => handleSetReminder(r)} sx={{ color: '#f97316' }}><AlarmAdd sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                                <Tooltip title={`Download ${isP ? 'Invoice' : 'Pay Stub'} PDF`}><IconButton size="small" onClick={() => handleDownloadPdf(r)} sx={{ color: '#ef4444' }}><PictureAsPdf sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                                <Tooltip title="Delete"><IconButton size="small" onClick={() => { setDeleteId(r._id); setDeleteType(r._type); }} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}><Delete sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                          {/* Expandable services row */}
                          {hasItems && (
                            <TableRow>
                              <TableCell sx={{ py: 0, border: 0 }} colSpan={15}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 1, px: 4, bgcolor: alpha('#f8fafc', 0.8) }}>
                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', mb: 1 }}>
                                      Services / Line Items ({r.items.length})
                                    </Typography>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.5 }}>Service</TableCell>
                                          {!isP && <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.5 }}>Provider</TableCell>}
                                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.5 }}>Collection $</TableCell>
                                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.5 }}>Rate</TableCell>
                                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.5 }}>Amount</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {r.items.map((item, idx) => (
                                          <TableRow key={idx} sx={{ '&:last-child td': { border: 0 } }}>
                                            <TableCell sx={{ fontSize: '0.78rem', py: 0.5 }}>{item.serviceName || item.description || '—'}</TableCell>
                                            {!isP && <TableCell sx={{ fontSize: '0.78rem', py: 0.5 }}>{item.providerName || '—'}</TableCell>}
                                            <TableCell align="right" sx={{ fontSize: '0.78rem', py: 0.5 }}>{fmt(item.collectionAmount)}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.78rem', py: 0.5 }}>
                                              {item.rateType === 'fixed' ? 'Fixed' : item.rate ? `${item.rate}%` : '—'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.78rem', py: 0.5 }}>{fmt(item.amount)}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination component="div" count={sortedFiltered.length} page={page} rowsPerPage={rpp}
                onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={e => { setRpp(+e.target.value); setPage(0); }}
                rowsPerPageOptions={[10, 20, 50, 100]} />
            </>
          )}
        </CardContent>
      </Card>
      {renderInvFilterPopover()}

      {/* ===== GENERATE INVOICE DIALOG (Multi-step) ===== */}
      <Dialog open={genOpen} onClose={() => { setGenOpen(false); resetGen(); }} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, background: genType === 'provider' ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'linear-gradient(135deg, #8b5cf6, #7c3aed)', borderRadius: 2 }}>
              <Receipt sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>
                {genStep === 0 ? 'Generate Invoice' : genStep === 1 ? 'Select Collections' : 'Review & Generate'}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>Step {genStep + 1} of 3</Typography>
            </Box>
          </Box>
          <IconButton onClick={() => { setGenOpen(false); resetGen(); }} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3, minHeight: 300 }}>
          {genStep === 0 && (
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Invoice Type</Typography>
                <ToggleButtonGroup size="small" exclusive fullWidth value={genType}
                  onChange={(_, v) => { if (v) { setGenType(v); setGenEntity(''); setSelectedProviderObj(null); } }}
                  sx={{ '& .MuiToggleButton-root': { flex: 1, py: 1, fontWeight: 700, textTransform: 'none' } }}>
                  <ToggleButton value="provider">Provider Invoice</ToggleButton>
                  <ToggleButton value="contractor">Contractor Pay Stub</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8' }}>
                    {genType === 'provider' ? 'Provider' : 'Contractor'} *
                  </Typography>
                  {genType === 'provider' && (
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={<Checkbox size="small" checked={includeInactiveProviders} onChange={(e) => setIncludeInactiveProviders(e.target.checked)} />}
                      label={<Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>Include inactive</Typography>}
                    />
                  )}
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  select
                  disabled={genType === 'provider' && providersLoading}
                  value={genEntity}
                  onChange={e => {
                    const id = e.target.value;
                    setGenEntity(id);
                    if (genType === 'provider') {
                      setSelectedProviderObj(providerOptions.find(p => String(p._id ?? p.id) === String(id)) || null);
                    }
                  }}>
                  <MenuItem value="">
                    {(genType === 'provider' && providersLoading) ? 'Loading providers…' : `Select ${genType}...`}
                  </MenuItem>
                  {entityList.map(e => <MenuItem key={e._id || e.id} value={e._id || e.id}>{fullName(e)}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Start Date (optional)</Typography>
                <TextField fullWidth size="small" type="date" value={genStartDate} onChange={e => setGenStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>End Date (optional)</Typography>
                <TextField fullWidth size="small" type="date" value={genEndDate} onChange={e => setGenEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              {genType === 'provider' && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Checkbox checked={genSplitByMonth} onChange={e => setGenSplitByMonth(e.target.checked)} size="small" />}
                    label={<Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Split invoices by month period (each month gets a separate invoice)</Typography>} />
                </Grid>
              )}
            </Grid>
          )}

          {genStep === 1 && (
            <Box>
              {pendingCollections.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Receipt sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                  <Typography sx={{ fontWeight: 600, color: '#64748b' }}>No uninvoiced collections found</Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography sx={{ fontWeight: 700 }}>{pendingCollections.length} Uninvoiced Collection{pendingCollections.length !== 1 ? 's' : ''} Found</Typography>
                    <FormControlLabel
                      control={<Checkbox checked={selectedCollections.size === pendingCollections.length}
                        indeterminate={selectedCollections.size > 0 && selectedCollections.size < pendingCollections.length}
                        onChange={toggleAllCols} size="small" />}
                      label={<Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>Select All</Typography>} />
                  </Box>
                  <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, maxHeight: 350 }}>
                    <Table size="small" stickyHeader>
                      <TableHead><TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell sx={{ fontWeight: 700 }}>Collection ID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Provider</TableCell>
                        {genType === 'contractor' && <TableCell sx={{ fontWeight: 700 }}>Contractor</TableCell>}
                        <TableCell sx={{ fontWeight: 700 }}>Period</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Services</TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {pendingCollections.map(c => {
                          const cId = c.id || c._id;
                          return (
                            <TableRow key={cId} hover onClick={() => toggleCol(cId)} sx={{ cursor: 'pointer', bgcolor: selectedCollections.has(cId) ? alpha('#3b82f6', 0.04) : 'transparent' }}>
                              <TableCell padding="checkbox"><Checkbox checked={selectedCollections.has(cId)} size="small" /></TableCell>
                              <TableCell><Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.82rem', color: '#d97706' }}>{c.collectionId}</Typography></TableCell>
                              <TableCell><Typography sx={{ fontSize: '0.82rem' }}>{fullName(c.provider)}</Typography></TableCell>
                              {genType === 'contractor' && <TableCell><Typography sx={{ fontSize: '0.82rem' }}>{fullName(c.contractor)}</Typography></TableCell>}
                              <TableCell><Typography sx={{ fontSize: '0.82rem', color: '#64748b' }}>{c.collectionPeriod ? new Date(c.collectionPeriod).toLocaleDateString() : '—'}</Typography></TableCell>
                              <TableCell align="right"><Chip label={c.services?.length || 0} size="small" sx={{ fontWeight: 700, height: 22 }} /></TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}

          {genStep === 2 && previewData && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Due Date (Optional)</Typography>
                  <TextField fullWidth size="small" type="date" value={genDueDate} onChange={e => setGenDueDate(e.target.value)} InputLabelProps={{ shrink: true }}
                    helperText="Leave empty to auto-calculate from provider's due date setting" />
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Notes</Typography>
                  <TextField fullWidth size="small" value={genNotes} onChange={e => setGenNotes(e.target.value)} placeholder="Optional notes..." />
                </Grid>
              </Grid>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Items Preview</Typography>
              <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Service</TableCell>
                    {genType === 'contractor' && <TableCell sx={{ fontWeight: 700 }}>Provider</TableCell>}
                    <TableCell sx={{ fontWeight: 700 }}>Collection</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Collection $</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Rate</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {(genType === 'contractor' ? editedItems : previewData.items || []).map((item, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ fontSize: '0.82rem' }}>{item.serviceName || '—'}</TableCell>
                        {genType === 'contractor' && <TableCell sx={{ fontSize: '0.82rem' }}>{item.providerName || '—'}</TableCell>}
                        <TableCell sx={{ fontSize: '0.82rem', fontFamily: 'monospace', color: '#d97706' }}>{item.collectionNumber || item.collectionId || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.82rem' }}>{fmt(item.collectionAmount)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.82rem' }}>
                          {item.rateType === 'fixed' ? 'Fixed' : item.rateType === 'hourly' ? `$${item.rate}/hr` : `${item.rate}%`}
                        </TableCell>
                        <TableCell align="right">
                          {genType === 'contractor' ? (
                            <TextField size="small" type="number" value={item.amount}
                              onChange={e => { const v = e.target.value; setEditedItems(prev => prev.map((it, idx) => idx === i ? { ...it, amount: v } : it)); }}
                              sx={{ width: 100, '& input': { textAlign: 'right', py: 0.5, fontSize: '0.82rem' } }}
                              InputProps={{ startAdornment: <InputAdornment position="start" sx={{ '& p': { fontSize: '0.78rem' } }}>$</InputAdornment> }} />
                          ) : (
                            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>{fmt(item.amount)}</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell colSpan={genType === 'contractor' ? 5 : 4} sx={{ fontWeight: 800, fontSize: '0.92rem' }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.92rem', color: '#3b82f6' }}>
                        {fmt(genType === 'contractor'
                          ? editedItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0)
                          : (previewData.items || []).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0))}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Box>{genStep > 0 && <Button startIcon={<ArrowBack />} onClick={() => setGenStep(s => s - 1)} sx={{ color: '#64748b', fontWeight: 600 }}>Back</Button>}</Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => { setGenOpen(false); resetGen(); }} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
            {genStep === 0 && (
              <Button variant="contained" onClick={handleFindCollections} disabled={!genEntity || genLoading}
                endIcon={genLoading ? <CircularProgress size={16} color="inherit" /> : <ArrowForward />}
                sx={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', fontWeight: 700, px: 3 }}>
                Find Collections
              </Button>
            )}
            {genStep === 1 && (
              <Button variant="contained" onClick={handlePreview} disabled={selectedCollections.size === 0 || genLoading}
                endIcon={genLoading ? <CircularProgress size={16} color="inherit" /> : <ArrowForward />}
                sx={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', fontWeight: 700, px: 3 }}>
                Preview
              </Button>
            )}
            {genStep === 2 && (
              <Button variant="contained" onClick={handleGenerate} disabled={genLoading}
                startIcon={genLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
                sx={{ background: genType === 'provider' ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'linear-gradient(135deg, #8b5cf6, #7c3aed)', fontWeight: 700, px: 4 }}>
                Generate {genType === 'provider' ? 'Invoice' : 'Pay Stub'}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* ===== VIEW DIALOG ===== */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontWeight: 700 }}>{viewType === 'provider' ? 'Invoice' : 'Pay Stub'} {viewData?.invoiceNumber}</Typography>
            <Chip label={viewType === 'provider' ? 'Provider Invoice' : 'Contractor Pay Stub'} size="small"
              sx={{ fontWeight: 700, height: 22, bgcolor: viewType === 'provider' ? alpha('#3b82f6', 0.1) : alpha('#8b5cf6', 0.1), color: viewType === 'provider' ? '#2563eb' : '#7c3aed' }} />
          </Box>
          <IconButton onClick={() => setViewOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {viewData && (
            <>
              <Grid container spacing={2} sx={{ mb: 3, mt: 0 }}>
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
                    {viewType === 'provider' ? 'Provider' : 'Contractor'}
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>{fullName(viewType === 'provider' ? viewData.provider : viewData.contractor)}</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Total</Typography>
                  <Typography sx={{ fontWeight: 700, color: '#3b82f6' }}>{fmt(viewData.total)}</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Paid</Typography>
                  <Typography sx={{ fontWeight: 600, color: '#059669' }}>{fmt(viewData.amountPaid)}</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Balance</Typography>
                  <Typography sx={{ fontWeight: 700, color: parseFloat(viewData.balance) > 0 ? '#dc2626' : '#059669' }}>{fmt(viewData.balance)}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Payment ID</Typography>
                  <Typography sx={{ fontWeight: 600, fontFamily: 'monospace', color: viewData.paymentId ? '#059669' : '#94a3b8' }}>{viewData.paymentId || '—'}</Typography>
                </Grid>
                {viewData.dueDate && (
                  <Grid item xs={3}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Due Date</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{new Date(viewData.dueDate).toLocaleDateString()}</Typography>
                  </Grid>
                )}
                {viewData.notes && (
                  <Grid item xs={9}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Notes</Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{viewData.notes}</Typography>
                  </Grid>
                )}
              </Grid>
              {viewData.items && viewData.items.length > 0 && (
                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                  <Table size="small">
                    <TableHead><TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Service</TableCell>
                      {viewType === 'contractor' && <TableCell sx={{ fontWeight: 700 }}>Provider</TableCell>}
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Collection $</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Rate</TableCell>
                      {viewType === 'contractor' && <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>}
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {viewData.items.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell>{item.serviceName || item.description || '—'}</TableCell>
                          {viewType === 'contractor' && <TableCell>{item.providerName || '—'}</TableCell>}
                          <TableCell align="right">{fmt(item.collectionAmount)}</TableCell>
                          <TableCell align="right">
                            {item.rateType === 'fixed' ? fmt(item.rate) : item.rateType === 'hourly' ? `$${item.rate}/hr` : item.rate ? `${item.rate}%` : '—'}
                          </TableCell>
                          {viewType === 'contractor' && <TableCell sx={{ textTransform: 'capitalize' }}>{item.rateType || '—'}</TableCell>}
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(item.amount)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell colSpan={viewType === 'contractor' ? 5 : 3} sx={{ fontWeight: 700 }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.95rem' }}>{fmt(viewData.total)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => { if (viewData) handleDownloadPdf({ _type: viewType, _id: viewData._id || viewData.id, invoiceNumber: viewData.invoiceNumber }); }}
            startIcon={<PictureAsPdf />} sx={{ color: '#ef4444', fontWeight: 600 }}>
            Download {viewType === 'provider' ? 'Invoice' : 'Pay Stub'} PDF
          </Button>
          <Button onClick={() => setViewOpen(false)} variant="contained" sx={{ fontWeight: 700 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ===== EDIT DIALOG ===== */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Edit sx={{ color: '#f59e0b' }} />
            <Typography sx={{ fontWeight: 700 }}>Edit {editType === 'provider' ? 'Invoice' : 'Pay Stub'}</Typography>
          </Box>
          <IconButton onClick={() => setEditOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Status</Typography>
              <TextField fullWidth size="small" select value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                {['draft', 'generated', 'approved', 'sent', 'paid', 'partial', 'overdue', 'cancelled'].map(s => (
                  <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Invoice Date</Typography>
              <TextField fullWidth size="small" type="date" value={editForm.invoiceDate} onChange={e => setEditForm(f => ({ ...f, invoiceDate: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Due Date</Typography>
              <TextField fullWidth size="small" type="date" value={editForm.dueDate} onChange={e => setEditForm(f => ({ ...f, dueDate: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Amount ($)</Typography>
              <TextField fullWidth size="small" type="number" value={editForm.subtotal} onChange={e => setEditForm(f => ({ ...f, subtotal: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Notes</Typography>
              <TextField fullWidth size="small" multiline rows={3} value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={actionLoading.editing}
            startIcon={actionLoading.editing ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', fontWeight: 700, px: 4 }}>
            {actionLoading.editing ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== PAYMENT DIALOG ===== */}
      <Dialog open={payOpen} onClose={() => setPayOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle sx={{ color: '#10b981' }} />
            <Typography sx={{ fontWeight: 700 }}>Record Payment</Typography>
          </Box>
          <IconButton onClick={() => setPayOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Amount</Typography>
              <TextField fullWidth size="small" type="number" value={payForm.amount} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start"><Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#64748b' }}>$</Typography></InputAdornment> }} />
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Payment Date</Typography>
              <TextField fullWidth size="small" type="date" value={payForm.paymentDate} onChange={e => setPayForm(f => ({ ...f, paymentDate: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Method</Typography>
              <TextField fullWidth size="small" select value={payForm.paymentMethod} onChange={e => setPayForm(f => ({ ...f, paymentMethod: e.target.value }))}>
                {['check', 'ach', 'wire', 'cash', 'other'].map(m => <MenuItem key={m} value={m} sx={{ textTransform: 'capitalize' }}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Reference / Check #</Typography>
              <TextField fullWidth size="small" value={payForm.referenceNumber} onChange={e => setPayForm(f => ({ ...f, referenceNumber: e.target.value }))} placeholder="Check # or reference" />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Notes</Typography>
              <TextField fullWidth size="small" multiline rows={2} value={payForm.notes} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPayOpen(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handlePayment} disabled={actionLoading.paying}
            startIcon={actionLoading.paying ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ bgcolor: '#10b981', fontWeight: 700, '&:hover': { bgcolor: '#059669' } }}>
            {actionLoading.paying ? 'Processing...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== DELETE DIALOG ===== */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Invoice?</DialogTitle>
        <DialogContent><Typography sx={{ color: 'text.secondary' }}>This action cannot be undone. Invoices with payments cannot be deleted.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleDelete} disabled={actionLoading.deleting}
            startIcon={actionLoading.deleting ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ bgcolor: '#ef4444', fontWeight: 700, '&:hover': { bgcolor: '#dc2626' } }}>
            {actionLoading.deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== COLLECTION DETAIL POPUP ===== */}
      <Dialog open={!!collectionPopup} onClose={() => setCollectionPopup(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 40, height: 40, background: 'linear-gradient(135deg, #f59e0b, #d97706)', borderRadius: 2 }}>
              <FolderOpen sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>Collection {collectionPopup?.collectionId}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>Collection Details</Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setCollectionPopup(null)} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          {collectionPopup && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Collection ID</Typography>
                  <Typography sx={{ fontWeight: 700, fontFamily: 'monospace', color: '#d97706' }}>{collectionPopup.collectionId}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Provider</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{fullName(collectionPopup.provider)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Contractor</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{fullName(collectionPopup.contractor) || '—'}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Period</Typography>
                  <Typography sx={{ fontWeight: 500 }}>{collectionPopup.collectionPeriod ? new Date(collectionPopup.collectionPeriod).toLocaleDateString() : '—'}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Status</Typography>
                  <Chip label={collectionPopup.status || 'pending'} size="small" sx={{ fontWeight: 700, height: 24, textTransform: 'capitalize' }} />
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Created By</Typography>
                  <Typography sx={{ fontWeight: 500 }}>{fullName(collectionPopup.createdBy) || '—'}</Typography>
                </Grid>
              </Grid>
              {collectionPopup.services && collectionPopup.services.length > 0 && (
                <>
                  <Typography sx={{ fontWeight: 700, mb: 1, fontSize: '0.85rem' }}>Services ({collectionPopup.services.length})</Typography>
                  <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead><TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Service</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Contractor</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Collection $</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Rate</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {collectionPopup.services.map((svc, i) => (
                          <TableRow key={i}>
                            <TableCell sx={{ fontSize: '0.82rem' }}>{svc.service?.name || svc.serviceName || '—'}</TableCell>
                            <TableCell sx={{ fontSize: '0.82rem' }}>{fullName(svc.contractor) || '—'}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.82rem' }}>{fmt(svc.collectionAmount)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.82rem' }}>{svc.billingRate ? `${svc.billingRate}%` : '—'}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{fmt(svc.billingAmount)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                          <TableCell colSpan={2} sx={{ fontWeight: 800 }}>Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(collectionPopup.services.reduce((s, svc) => s + (parseFloat(svc.collectionAmount) || 0), 0))}</TableCell>
                          <TableCell />
                          <TableCell align="right" sx={{ fontWeight: 800, color: '#3b82f6' }}>{fmt(collectionPopup.services.reduce((s, svc) => s + (parseFloat(svc.billingAmount) || 0), 0))}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
              {collectionPopup.providerInvoice && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: alpha('#3b82f6', 0.04), borderRadius: 2 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>Provider Invoice: <span style={{ color: '#3b82f6', fontFamily: 'monospace' }}>{collectionPopup.providerInvoice.invoiceNumber}</span> — Status: {collectionPopup.providerInvoice.status}</Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCollectionPopup(null)} variant="contained" sx={{ fontWeight: 700 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ===== ENTITY DETAIL POPUP ===== */}
      <Dialog open={!!entityPopup} onClose={() => setEntityPopup(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 40, height: 40, background: entityPopup?._entityType === 'provider' ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'linear-gradient(135deg, #8b5cf6, #7c3aed)', borderRadius: 2 }}>
              {entityPopup?._entityType === 'provider' ? <Business sx={{ fontSize: 20 }} /> : <People sx={{ fontSize: 20 }} />}
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>{fullName(entityPopup)}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{entityPopup?._entityType === 'provider' ? 'Provider' : 'Contractor'}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => {
                setEntityPopup(null);
                navigate(entityPopup?._entityType === 'provider' ? '/providers' : '/contractors');
              }} sx={{ color: '#f59e0b' }}>
                <Edit sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <IconButton onClick={() => setEntityPopup(null)} size="small"><Close /></IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          {entityPopup && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>ID</Typography>
                <Typography sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{entityPopup.providerId || entityPopup.contractorId || '—'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Email</Typography>
                <Typography sx={{ fontWeight: 500 }}>{entityPopup.email || '—'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Phone</Typography>
                <Typography sx={{ fontWeight: 500 }}>{entityPopup.phone || '—'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Status</Typography>
                <Chip label={entityPopup.isActive !== false ? 'Active' : 'Inactive'} size="small"
                  sx={{ fontWeight: 700, height: 24, bgcolor: entityPopup.isActive !== false ? alpha('#10b981', 0.1) : alpha('#ef4444', 0.1), color: entityPopup.isActive !== false ? '#059669' : '#dc2626' }} />
              </Grid>
              {entityPopup.groupName && (
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Group / Practice</Typography>
                  <Typography sx={{ fontWeight: 500 }}>{entityPopup.groupName}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEntityPopup(null)} sx={{ color: '#64748b', fontWeight: 600 }}>Close</Button>
          <Button variant="contained" onClick={() => { setEntityPopup(null); navigate(entityPopup?._entityType === 'provider' ? '/providers' : '/contractors'); }}
            startIcon={<Edit />} sx={{ fontWeight: 700 }}>
            Go to {entityPopup?._entityType === 'provider' ? 'Providers' : 'Contractors'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== REMINDER DIALOG ===== */}
      <Dialog open={reminderOpen} onClose={() => setReminderOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsActive sx={{ color: '#f97316' }} />
            <Typography sx={{ fontWeight: 700 }}>Set Reminder</Typography>
          </Box>
          <IconButton onClick={() => setReminderOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Title</Typography>
              <TextField fullWidth size="small" value={reminderForm.title} onChange={e => setReminderForm(f => ({ ...f, title: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Reminder Date</Typography>
              <TextField fullWidth size="small" type="date" value={reminderForm.reminderDate} onChange={e => setReminderForm(f => ({ ...f, reminderDate: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Priority</Typography>
              <TextField fullWidth size="small" select value={reminderForm.priority} onChange={e => setReminderForm(f => ({ ...f, priority: e.target.value }))}>
                {['low', 'medium', 'high', 'urgent'].map(p => <MenuItem key={p} value={p} sx={{ textTransform: 'capitalize' }}>{p}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Description</Typography>
              <TextField fullWidth size="small" multiline rows={3} value={reminderForm.description} onChange={e => setReminderForm(f => ({ ...f, description: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setReminderOpen(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveReminder}
            sx={{ background: 'linear-gradient(135deg, #f97316, #ea580c)', fontWeight: 700, px: 4 }}>
            Set Reminder
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Invoices;
